/**
 * Renders automation audit summaries beneath a timeline event.
 *
 * Shows the automation name (as an EntityBadge) and the full audit
 * summary (as MarkdownContent with entity tag rendering).
 */

import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import type { EntityBadgeState } from '@/lib/crm/components/badges/types';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';

import { useViewStateLookups } from '../../context/TimelineLookupsContext';
import type { HydratedEntity } from '../../types';
import { MARKDOWN_ALERT_CLASSNAME } from './MarkdownAlertStyle';

interface AuditSummaryProps {
  audits: HydratedEntity[];
}

function AuditEntry({ audit }: { audit: HydratedEntity }) {
  const { orgId } = useParams<{ orgId: string }>();
  const orgIdNum = orgId ? Number(orgId) : undefined;
  const openSlideout = useOpenSlideoutOptional();
  const { getEntity } = useViewStateLookups();

  const rawSummary = audit.properties?.ahoy_summary?.value;
  const summary = typeof rawSummary === 'string' ? rawSummary : undefined;

  // Resolve automation from audit's associations → entities pool
  const automation = useMemo<HydratedEntity | undefined>(() => {
    const assoc = audit.associations?.find(
      a => a.association_type_name === 'ahoy_audit_to_automation'
    );
    if (!assoc) {
      return undefined;
    }
    return getEntity(assoc.target_entity_id);
  }, [audit.associations, getEntity]);

  const automationBadgeState = useMemo<EntityBadgeState | null>(() => {
    if (!automation) {
      return null;
    }
    return {
      kind: 'data',
      data: {
        id: String(automation.entity_id),
        entityType: automation.entity_type_name,
        displayName: automation.ahoy_display_name ?? 'Automation',
      },
    };
  }, [automation]);

  const handleAutomationClick = useCallback(() => {
    if (automation) {
      openSlideout?.(String(automation.entity_id));
    }
  }, [openSlideout, automation]);

  return (
    <div className='rounded-md border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] px-3 py-2'>
      {automationBadgeState && (
        <div className='mb-1'>
          <EntityBadge
            state={automationBadgeState}
            onClick={openSlideout ? handleAutomationClick : undefined}
          />
        </div>
      )}
      {summary && orgIdNum && (
        <MarkdownContent
          content={summary}
          orgId={orgIdNum}
          size='compact'
          className={MARKDOWN_ALERT_CLASSNAME}
        />
      )}
    </div>
  );
}

export function AuditSummary({ audits }: AuditSummaryProps) {
  if (audits.length === 0) {
    return null;
  }

  return (
    <div className='mt-2 space-y-2'>
      {audits.map(audit => (
        <AuditEntry key={String(audit.entity_id)} audit={audit} />
      ))}
    </div>
  );
}
